import React, { useState, useCallback, useMemo } from 'react';
import { useCartContext } from '../../peregrine/lib/context/cart';
import { useAppContext } from '../../peregrine/lib/context/app';
import { useToasts } from '../../peregrine/lib/Toasts';
import { useMutation } from '@apollo/client';
import { useAwaitQuery } from '../../peregrine/lib/hooks/useAwaitQuery';
import { updateProductsInWishlistMutation } from '../../queries/updateProductsInWishlist.gql';
import Icon from '../../components/Icon';
import { AlertCircle as AlertCircleIcon } from 'react-feather';
import groupBy from '../../lib/util/groupBy';
import { ADD_MULTIPLE_SIMPLE_MUTATION } from '../../components/ProductFullDetail/productFullDetail.gql';
import GET_CART_DETAILS_QUERY from '../../queries/getCartDetails.graphql';
import CREATE_CART_MUTATION from '../../queries/createCart.graphql';

const errorIcon = (
    <Icon
        src={AlertCircleIcon}
        attrs={{
            width: 18
        }}
    />
);

export const wishlistToConfiguratorsArray = wishlist =>
    (wishlist || [])
        .map(({ id: wishlistId, items_v2: { items } }) => {
            const configurators = items
                .map(({ id: wishlistItemId, description }) => {
                    try {
                        return JSON.parse(description).map(product => ({
                            wishlistId,
                            wishlistItemId,
                            group: _('Default project'),
                            ...product
                        }));
                    } catch (err) {
                        return null;
                    }
                })
                .filter(item => !!item);

            return configurators.flat();
        })
        .flat()
        .sort((a, b) => new Date(b.date) - new Date(a.date));

export const useSavedConfigurators = props => {
    const { wishlists } = props;

    const groupedWishlists = groupBy(
        wishlistToConfiguratorsArray(wishlists),
        'group'
    );

    const [isRemovingFromWishlist, setIsRemovingFromWishlist] = useState(false);
    const [, { addToast }] = useToasts();

    const fetchCartDetails = useAwaitQuery(GET_CART_DETAILS_QUERY);
    const [fetchCartId] = useMutation(CREATE_CART_MUTATION);

    const [updateProductInWishlist] = useMutation(
        updateProductsInWishlistMutation
    );

    const removeFromWishlist = async (
        wishlistId,
        wishlistItemId,
        productId
    ) => {
        setIsRemovingFromWishlist(true);

        try {
            const configurators = JSON.parse(
                wishlists
                    .find(({ id }) => id == wishlistId)
                    .items_v2.items.find(item => item.id == wishlistItemId)
                    .description
            ).filter(product => product.id != productId);

            const response = await updateProductInWishlist({
                variables: {
                    wishlistId: wishlistId,
                    itemId: wishlistItemId,
                    configuration: JSON.stringify(configurators)
                }
            });

            const { user_errors } = response.data.updateProductsInWishlist;

            if (user_errors.length) {
                throw Error(
                    user_errors.map(({ message }) => message).join('\n')
                );
            }
        } catch (error) {
            console.error(error);
            addToast({
                type: 'error',
                icon: errorIcon,
                message: error + '',
                dismissable: true,
                timeout: 10000
            });
        } finally {
            setIsRemovingFromWishlist(false);
        }
    };

    const updateGroupTitle = async (newTitle, oldTitle) => {
        try {
            const configurators = Object.values(groupedWishlists)
                .flat()
                .map(item => {
                    if (item.group == oldTitle) {
                        item.group = newTitle;
                    }
                    return item;
                });

            const { wishlistId, wishlistItemId } = configurators[0];

            const response = await updateProductInWishlist({
                variables: {
                    wishlistId: wishlistId,
                    itemId: wishlistItemId,
                    configuration: JSON.stringify(configurators)
                }
            });

            const { user_errors } = response.data.updateProductsInWishlist;

            if (user_errors.length) {
                throw Error(
                    user_errors.map(({ message }) => message).join('\n')
                );
            }
        } catch (error) {
            console.error(error);
            addToast({
                type: 'error',
                icon: errorIcon,
                message: error + '',
                dismissable: true,
                timeout: 10000
            });
        }
    };

    const moveToGroup = async (
        wishlistId,
        wishlistItemId,
        id,
        newGroupName
    ) => {
        try {
            const configurators = JSON.parse(
                wishlists
                    .find(({ id }) => id == wishlistId)
                    .items_v2.items.find(item => item.id == wishlistItemId)
                    .description
            ).map(product => {
                if (product.id == id) {
                    product.group = newGroupName;
                }
                return product;
            });

            const response = await updateProductInWishlist({
                variables: {
                    wishlistId: wishlistId,
                    itemId: wishlistItemId,
                    configuration: JSON.stringify(configurators)
                }
            });

            const { user_errors } = response.data.updateProductsInWishlist;

            if (user_errors.length) {
                throw Error(
                    user_errors.map(({ message }) => message).join('\n')
                );
            }
        } catch (error) {
            console.error(error);
            addToast({
                type: 'error',
                icon: errorIcon,
                message: error + '',
                dismissable: true,
                timeout: 10000
            });
        }
    };

    const [{ isAddingItem }, { addItemToCart }] = useCartContext();
    const [, { toggleDrawer }] = useAppContext();
    const [addItemMutation] = useMutation(ADD_MULTIPLE_SIMPLE_MUTATION);

    const addGroupToCart = useCallback(
        async group => {
            const projects = groupedWishlists[group];

            console.log(projects);

            const payload = {
                item: {
                    // fake item to force addItemToCart() to use set the correct variables
                    // to the mutation (see asyncActions.js)
                    __typename: 'BundleProduct'
                },
                cartItems: projects.map(
                    ({
                        sku,
                        quantity = 1,
                        customPrice,
                        customOptionId,
                        customOptionValue
                    }) => ({
                        data: {
                            sku,
                            quantity,
                            custom_price: customPrice
                        },
                        customizable_options: [
                            {
                                id: customOptionId,
                                value_string: customOptionValue
                            }
                        ]
                    })
                )
            };

            console.log(payload);

            try {
                await addItemToCart({
                    ...payload,
                    addItemMutation,
                    fetchCartDetails,
                    fetchCartId
                });

                toggleDrawer('cart');
            } catch (error) {
                console.error(error);
                const errorMessage = error.message.replace(
                    'Error: GraphQL error: ',
                    ''
                );

                addToast({
                    type: 'error',
                    icon: errorIcon,
                    message: errorMessage,
                    dismissable: true,
                    timeout: 10000
                });
            }
        },
        [groupedWishlists, addItemMutation, addToast]
    );

    const totalPrices = useMemo(() => {
        return new Map(
            Object.entries(groupedWishlists).map(([group, items]) => [
                group,
                items.reduce((acc, current) => acc + current.customPrice, 0)
            ])
        );
    }, [groupedWishlists]);

    return {
        wishlists: groupedWishlists,
        groups: Object.keys(groupedWishlists),
        totalPrices,
        removeFromWishlist,
        updateGroupTitle,
        moveToGroup,
        addGroupToCart,
        isLoading: isRemovingFromWishlist || isAddingItem
    };
};
